<template>
  <FormField :id="id" :label="label" :info="info" :hint="hint" :error="error">
    <div class="relative">
      <select
        :id="id"
        :value="modelValue"
        :disabled="disabled"
        :aria-invalid="error ? true : undefined"
        :aria-describedby="describedBy"
        class="h-14 w-full cursor-pointer appearance-none rounded-xl border px-4 pr-12 text-base font-normal outline-none transition-colors focus:border-blue-bright disabled:cursor-not-allowed"
        :class="[disabled ? 'bg-surface-muted' : 'bg-white', error ? 'border-red-600' : 'border-line', textColour]"
        @change="emit('update:modelValue', ($event.target as HTMLSelectElement).value)"
      >
        <option value="" disabled>Maak een keuze</option>
        <option v-for="option in options" :key="option.value" :value="option.value">{{ option.label }}</option>
      </select>
      <ChevronDownIcon
        class="pointer-events-none absolute top-1/2 right-4 size-6 -translate-y-1/2"
        :class="disabled ? 'text-muted' : 'text-label'"
        aria-hidden="true"
      />
    </div>
  </FormField>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import ChevronDownIcon from '@/assets/svg/chevron-down.svg?component'
import FormField from '@/components/form/FormField.vue'

const props = defineProps<{
  id: string
  label: string
  options: { value: string; label: string }[]
  /** Empty string means nothing chosen yet; the design starts without a preselected option. */
  modelValue: string
  info?: string
  /** Shown under the field, for a note too long to survive the single line of a closed select. */
  hint?: string
  error?: string
  /** Frozen while the calculation runs: greyed out, but the chosen option stays readable. */
  disabled?: boolean
}>()

const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

const describedBy = computed(() => {
  const ids = [props.hint ? `${props.id}-hint` : null, props.error ? `${props.id}-error` : null].filter(Boolean)

  return ids.length > 0 ? ids.join(' ') : undefined
})

// Nothing chosen yet reads as placeholder text; a frozen field steps back without going pale.
const textColour = computed(() => {
  if (props.modelValue === '') {
    return 'text-muted'
  }

  return props.disabled ? 'text-body' : 'text-label'
})
</script>
